import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'src/common';
import styles from './index.module.scss';
import CompaniesForm from 'src/shared/OrgsSearch';
import CompaniesList from './CompaniesList';
import { Description } from 'src/shared/Blocks';
import { useLoadMp, useUpdateMpCompaniesIds } from 'src/models/matchingProfiles/hooks';
import { trackMPEditAddBuyers } from 'src/segment/events';
import { xor } from 'lodash';

interface Props {
  disabled?: boolean;
}
export const CompaniesFilter: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation();
  const { data: mp, loading } = useLoadMp();

  const [updateMpCompaniesIds] = useUpdateMpCompaniesIds();
  const companiesIds = useMemo(() => {
    return mp?.buyerOrgIds ?? [];
  }, [mp]);
  const orgSearchStrings = useMemo(() => {
    return mp?.buyerSearchStrings ?? [];
  }, [mp]);
  const [lastAddedOrgIds, setLastAddedOrgIds] = useState<string[]>([]);

  const onAddOrgs = useCallback(
    (exact?: string[], prefix?: string[]) => {
      if (xor(exact ?? [], companiesIds).length !== 0 || xor(prefix ?? [], orgSearchStrings).length !== 0) {
        if (mp) {
          trackMPEditAddBuyers({ id: mp.id, name: mp.name });
        }
        const uniqueCompaniesIds = [...new Set([...(exact ?? []), ...companiesIds])];
        const uniqueSearchStrings = [...new Set([...(prefix ?? []), ...orgSearchStrings])];
        updateMpCompaniesIds(uniqueCompaniesIds, uniqueSearchStrings);
        exact && setLastAddedOrgIds(() => exact);
      }
    },
    [companiesIds, mp, orgSearchStrings, updateMpCompaniesIds]
  );

  const onRemoveOrgs = useCallback(
    (updatingIds, updatingSearchStrings, onComplete) =>
      updateMpCompaniesIds(updatingIds, updatingSearchStrings, onComplete),
    [updateMpCompaniesIds]
  );

  return loading ? (
    <Skeleton loading active />
  ) : (
    <div className={styles.companiesFilter}>
      <div className={styles.descriptionContainer}>
        <Description>{t('Tenders.BuyersFilter.buyersDesc')}</Description>
      </div>
      <CompaniesForm
        disabled={!!disabled}
        exact={companiesIds}
        prefix={orgSearchStrings}
        mode={'multiple'}
        onSelect={onAddOrgs}
      />
      <CompaniesList
        disabled={!!disabled}
        orgNumbers={companiesIds}
        orgSearchStrings={orgSearchStrings}
        lastAddedOrgIds={lastAddedOrgIds}
        removeOrgs={onRemoveOrgs}
      />
    </div>
  );
};

export default CompaniesFilter;
